import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'

import { ArrowLeft, ArrowRight } from 'lucide-react'

import { SEO } from '@/components/SEO'

import { SmartButton } from '../../components/ui-custom/SmartButton'

interface DashboardSectionProps {
  title?: string
  desc?: string
  children?: React.ReactNode
  headerActions?: React.ReactNode
  className?: string
  maxWidth?: string // e.g. "max-w-3xl" or custom value like "800px"
  align?: 'left' | 'center' | 'right' // alignment of the container
  backHref?: string
  onBack?: () => void
  backLabel?: string
  [key: string]: any
}

export default function DashboardContent({
  title,
  desc,
  children,
  headerActions,
  className = '',
  maxWidth = 'max-w-7xl', // default max width (tailwind class)
  align = 'center', // default alignment
  backHref,
  onBack,
  backLabel,
  ...props
}: DashboardSectionProps) {
  const alignMap = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const BackArrow = isRTL ? ArrowRight : ArrowLeft

  return (
    <>
      <SEO title={title} description={desc} />
      <div className={`flex w-full ${alignMap[align]} p-4 sm:p-6 md:gap-6 md:p-8`} {...props}>
        <div className={`flex w-full flex-col ${maxWidth} ${className}`}>
          {/* Header */}
          {(title || desc || headerActions || backHref || onBack) && (
            <div className='xs:gap-4 mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center'>
              <div className='flex gap-4'>
                {(backHref || onBack) && (
                  <SmartButton
                    type='button'
                    variant='outline'
                    onClick={onBack || (() => navigate(backHref!))}
                    className='h-8 w-8 bg-transparent sm:h-10 sm:w-10 sm:p-4'
                  >
                    <BackArrow className='h-4 w-4' />
                    {backLabel}
                  </SmartButton>
                )}
                {(title || desc) && (
                  <div className='space-y-1'>
                    {title && <h1 className='text-primary text-2xl font-semibold'>{title}</h1>}
                    {desc && <p className='text-muted-foreground mt-1 text-sm'>{desc}</p>}
                  </div>
                )}
              </div>
              {headerActions && <div className='flex flex-wrap items-center justify-end gap-2'>{headerActions}</div>}
            </div>
          )}

          {/* Content */}
          <div className='w-full space-y-3 sm:space-y-4'>{children}</div>
        </div>
      </div>
    </>
  )
}
